import { Link, useNavigate } from "react-router-dom";
import { Calendar, Play, ArrowRight, Zap, ClipboardList, Eye, ReceiptText, Wrench, Building2, Hammer, Sparkles, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/site/SectionHeading";
import PricingCards from "@/components/site/PricingCards";
import { useBookDemo } from "@/context/BookDemoContext";
import { useVideoModal } from "@/context/VideoModalContext";
import StepByStepWorkflow from "@/components/site/StepByStepWorkflow";

const SHORT_BENEFITS = [
    { icon: Zap, label: "Hurtigere svar" },
    { icon: ClipboardList, label: "Mindre administration" },
    { icon: Eye, label: "Mere overblik" },
    { icon: ReceiptText, label: "Klar til faktura" },
];

const HOME_INDUSTRIES = [
    { icon: Zap, name: "Elektriker", desc: "Styr opgaver og service med fuldt overblik." },
    { icon: Wrench, name: "VVS", desc: "Planlæg opgaver og materialer effektivt." },
    { icon: Hammer, name: "Tømrer", desc: "Hold styr på projekter, tid og dokumentation." },
    { icon: Sparkles, name: "Service", desc: "Hurtig respons og smarte arbejdsgange." },
    { icon: Building2, name: "Ejendomsservice", desc: "Én løsning til drift og vedligehold." },
];

export default function Home() {
    const { open: openDemo } = useBookDemo();
    const { open: openVideo } = useVideoModal();
    const navigate = useNavigate();
    const goToBuilder = () => navigate("/priser#builder");

    return (
        <div data-testid="page-home">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute -top-20 -right-32 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-blue-200/40 via-indigo-200/40 to-purple-200/30 blur-3xl pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 lg:pt-14 lg:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-6">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-slate-700 text-[11px] font-semibold uppercase tracking-wider border border-slate-200 shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Skandinavisk SaaS til servicevirksomheder
                            </span>
                            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 leading-[1.05]">
                                Mindre kontorkaos.<br />
                                <span className="gradient-text">Flere opgaver færdige.</span>
                            </h1>
                            <p className="mt-4 text-base lg:text-lg font-medium text-slate-700">
                                Fra kundeopkald til færdig opgave
                            </p>
                            <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed max-w-xl">
                                MyServiceHelper samler kundekontakt, opgavestyring, arbejdstid, materialer og opdateringer i ét system, så du sparer tid, arbejder smartere og skaber glade kunder.
                            </p>

                            <div className="mt-6 flex flex-wrap gap-3">
                                <Button
                                    data-testid="hero-book-demo-btn"
                                    onClick={() => openDemo()}
                                    className="rounded-full h-11 px-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl transition-all"
                                >
                                    <Calendar className="w-4 h-4 mr-2" />
                                    Book gratis demo
                                </Button>
                                <Button
                                    data-testid="hero-see-product-btn"
                                    onClick={openVideo}
                                    variant="outline"
                                    className="rounded-full h-11 px-5 border-slate-300 text-slate-900 hover:bg-slate-50"
                                >
                                    <Play className="w-4 h-4 mr-2" />
                                    Se produktet
                                </Button>
                            </div>

                            <ul className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-xl">
                                {SHORT_BENEFITS.map((b, i) => {
                                    const Icon = b.icon;
                                    return (
                                        <li key={i} data-testid={`hero-benefit-${i}`} className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-white border border-slate-200/70 shadow-sm">
                                            <Icon className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                                            <span className="text-[11px] sm:text-xs font-medium text-slate-700">{b.label}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        <div className="lg:col-span-6">
                            <StepByStepWorkflow />
                        </div>
                    </div>
                </div>
            </section>

            {/* Industries preview */}
            <section data-testid="home-industries" className="py-16 sm:py-20 bg-white border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="Brancher" title="Bygget til" gradientWord="håndværkerbranchen" subtitle="Én løsning, der passer til din hverdag." />

                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {HOME_INDUSTRIES.map((ind) => {
                            const Icon = ind.icon;
                            return (
                                <div
                                    key={ind.name}
                                    data-testid={`industry-card-${ind.name.toLowerCase()}`}
                                    className="group bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-200 transition-all"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 inline-flex items-center justify-center ring-1 ring-blue-100">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <h3 className="mt-4 text-base font-semibold text-slate-900">{ind.name}</h3>
                                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{ind.desc}</p>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-8 text-center">
                        <Link to="/brancher" data-testid="home-industries-cta" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800">
                            Se alle brancher <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Pricing preview */}
            <section className="py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="Priser" title="Enkle priser uden" gradientWord="unødigt rod" subtitle="Vælg det setup der passer til jeres arbejdsgang — og udvid når behovet vokser." />

                    <div className="mt-12 max-w-4xl mx-auto">
                        <PricingCards onSelectPlan={goToBuilder} />
                    </div>

                    {/* Extra flows hint */}
                    <div data-testid="home-extras-hint" className="mt-8 max-w-4xl mx-auto">
                        <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                            <div className="flex items-start gap-3 min-w-0">
                                <span className="shrink-0 w-9 h-9 rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 inline-flex items-center justify-center">
                                    <Plus className="w-4 h-4" />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-slate-900">Skalér op med ekstra flows</p>
                                    <p className="text-xs text-slate-600 mt-0.5">
                                        Tilkøb ekstra kunde- og medarbejderflows, minutter og kommandoer i setup-builderen.
                                    </p>
                                </div>
                            </div>
                            <Link to="/priser">
                                <Button data-testid="home-pricing-cta" variant="outline" className="rounded-full h-10 px-4 border-slate-300 text-slate-900 hover:bg-slate-50">
                                    Se alle prisdetaljer <ArrowRight className="w-4 h-4 ml-1.5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-8 sm:p-12 text-white">
                        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-blue-500/30 blur-3xl" />
                        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-purple-500/30 blur-3xl" />
                        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                            <div>
                                <h3 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
                                    Klar til at se MyServiceHelper i praksis?
                                </h3>
                                <p className="mt-3 text-white/70 max-w-lg text-sm sm:text-base">
                                    Book en gratis demo, så gennemgår vi flowet fra kundehenvendelse til færdig opgave.
                                </p>
                            </div>
                            <div className="md:justify-self-end">
                                <Button
                                    data-testid="home-bottom-cta-btn"
                                    onClick={() => openDemo()}
                                    className="rounded-full h-12 px-6 bg-white text-slate-900 hover:bg-slate-100"
                                >
                                    <Calendar className="w-4 h-4 mr-2" />
                                    Book gratis demo
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
